// New currency hook exposing { baseCurrency, localCurrency, fxRate, formatBase, formatLocal, formatBoth }.
// Shares state with useCurrencyStore so the header CurrencySwitcher override flows in automatically.
import { useTranslation } from "react-i18next";
import { useCountry } from "@/hooks/useCountry";
import { useFxRates } from "@/hooks/useFxRates";
import { defaultForCountry } from "@/hooks/useCurrency";
import {
  useCurrencyStore,
  formatMinor,
  roundToAnchor,
  type CurrencyCode,
} from "@/lib/pricing/currency";

const ZERO_DECIMAL: CurrencyCode[] = [
  "NGN", "XOF", "XAF", "GNF", "KES", "ARS", "COP", "IDR", "VND",
];

export interface UseCurrencyResult {
  baseCurrency: "EUR";
  /** Geo-detected local currency (independent of any manual override). */
  detectedLocal: CurrencyCode;
  /** Whether the user has explicitly chosen a currency (overrides geo). */
  manuallySet: boolean;
  /** Currency to display: manual choice if set, otherwise the geo local. */
  localCurrency: CurrencyCode;
  fxRate: number;
  formatBase: (eurMajor: number) => string;
  formatLocal: (eurMajor: number) => string;
  formatBoth: (eurMajor: number) => string;
  setOverride: (c: CurrencyCode) => void;
}

export function useCurrency(): UseCurrencyResult {
  const { country } = useCountry();
  const override = useCurrencyStore((s) => s.currency);
  const manuallySet = useCurrencyStore((s) => s.manuallySet);
  const setCurrency = useCurrencyStore((s) => s.setCurrency);
  const rates = useFxRates();
  const { i18n } = useTranslation();
  const locale = i18n.language;

  const detectedLocal: CurrencyCode = defaultForCountry(country) ?? "EUR";
  const localCurrency: CurrencyCode = manuallySet ? override : detectedLocal;
  const baseCurrency = "EUR" as const;
  const fxRate = rates[localCurrency] ?? 1;

  const formatBase = (eurMajor: number) =>
    formatMinor(Math.round(eurMajor * 100), baseCurrency, locale);

  const formatLocal = (eurMajor: number) => {
    const localMajor = roundToAnchor(eurMajor * fxRate, localCurrency);
    const minor = ZERO_DECIMAL.includes(localCurrency)
      ? localMajor * 100
      : Math.round(localMajor * 100);
    return formatMinor(minor, localCurrency, locale);
  };

  const formatBoth = (eurMajor: number) =>
    localCurrency === baseCurrency
      ? formatBase(eurMajor)
      : `${formatBase(eurMajor)} (≈ ${formatLocal(eurMajor)})`;

  const setOverride = (c: CurrencyCode) => setCurrency(c, { manual: true });

  return {
    baseCurrency,
    detectedLocal,
    manuallySet,
    localCurrency,
    fxRate,
    formatBase,
    formatLocal,
    formatBoth,
    setOverride,
  };
}
